import React, { FC, useCallback, useEffect, useRef } from 'react'
// import './styles.scss'
import { Waypoint } from 'react-waypoint'
// import { useWindowSize } from 'usehooks-ts'
import ContentGrid from 'components/ContentGrid'
import styled from 'styled-components'
import {
  ContentWidth,
  Left,
  LeftAndText,
  Right,
  RightAndText,
  TextWidth,
  TextWidthExtended,
} from 'components/Container'

const isMobile = window.innerWidth <= 599
const isFullWidthMobile = window.innerWidth <= 711

type PlayerProps = {
  mobileVideo?: string
  desktopVideo?: string
  mobilePoster?: string
  desktopPoster?: string
  controls?: 'true' | 'false'
}

/**
 * The video element + its Waypoint trigger, extracted so the autoplay
 * handling lives in exactly one place instead of being repeated per width.
 */
const AutoplayVideo: FC<PlayerProps> = ({
  mobileVideo,
  desktopVideo,
  mobilePoster,
  desktopPoster,
  controls = 'false',
}) => {
  const vidRef = useRef<HTMLVideoElement>(null)
  const playPromise = useRef<Promise<void> | null>(null)

  // React sets `muted` as a property but never writes the attribute, and iOS
  // checks the attribute when deciding whether autoplay is allowed.
  useEffect(() => {
    const v = vidRef.current
    if (!v) return
    v.muted = true
    v.defaultMuted = true
    v.setAttribute('muted', '')
  }, [])

  const play = useCallback(() => {
    const v = vidRef.current
    if (!v) return
    v.muted = true
    const p = v.play()
    playPromise.current = p ?? null
    // Swallow AbortError / NotAllowedError so a rejected promise doesn't
    // surface as an unhandled rejection.
    p?.catch(() => {})
  }, [])

  const pause = useCallback(async () => {
    const v = vidRef.current
    if (!v) return
    // Let a pending play() settle first — pausing mid-promise aborts it and
    // leaves the video stuck on the poster.
    try {
      await playPromise.current
    } catch {
      /* already handled above */
    }
    if (!v.paused) v.pause()
  }, [])

  // Stop playback if the component unmounts while a play() is in flight.
  useEffect(() => {
    return () => {
      const v = vidRef.current
      Promise.resolve(playPromise.current)
        .catch(() => {})
        .then(() => {
          if (v && !v.paused) v.pause()
        })
    }
  }, [])

  return (
    <Waypoint scrollableAncestor={window} onEnter={play} onLeave={pause}>
      <video
        ref={vidRef}
        {...(controls === 'true' ? { controls: true } : {})}
        autoPlay
        muted
        playsInline
        loop
        preload='metadata'
        poster={isMobile ? mobilePoster : desktopPoster}
      >
        {isMobile && mobileVideo && (
          <source src={mobileVideo} type='video/mp4' />
        )}
        {desktopVideo && <source src={desktopVideo} type='video/mp4' />}
      </video>
    </Waypoint>
  )
}

export const Video: FC<{
  mobileVideo?: string
  desktopVideo?: string
  mobilePoster?: string
  desktopPoster?: string
  title?: string
  lead?: string
  width?:
    | 'full'
    | 'content'
    | 'text'
    | 'left'
    | 'right'
    | 'leftandtext'
    | 'rightandtext'
    | 'textextended'
  caption?: string
  source?: string
  labelType?: 'top' | 'bottom'
  controls?: 'true' | 'false'
}> = ({
  mobileVideo,
  desktopVideo,
  mobilePoster,
  desktopPoster,
  title,
  lead,
  width,
  caption,
  source,
  labelType = 'top',
  controls = 'false',
}) => {
  const player = (
    <AutoplayVideo
      mobileVideo={mobileVideo}
      desktopVideo={desktopVideo}
      mobilePoster={mobilePoster}
      desktopPoster={desktopPoster}
      controls={controls}
    />
  )

  return (
    <>
      {(title || caption) && labelType === 'top' && (
        <ContentGrid>
          <TextWidth>
            <>
              {title && <Title>{title}</Title>}
              {lead && <Lead className={labelType}>{lead}</Lead>}
            </>
          </TextWidth>
        </ContentGrid>
      )}

      {width === 'full' && <VideoContainer>{player}</VideoContainer>}

      {width === 'content' && (
        <>
          {isFullWidthMobile ? (
            <VideoContainer>{player}</VideoContainer>
          ) : (
            <ContentGrid>
              <ContentWidth>
                <VideoContainer>{player}</VideoContainer>
              </ContentWidth>
            </ContentGrid>
          )}
        </>
      )}

      {width === 'text' && (
        <ContentGrid>
          <TextWidth>
            <VideoContainer>{player}</VideoContainer>
          </TextWidth>
        </ContentGrid>
      )}

      {width === 'textextended' && (
        <ContentGrid>
          <TextWidthExtended>
            <VideoContainer>{player}</VideoContainer>
          </TextWidthExtended>
        </ContentGrid>
      )}

      {width === 'left' && (
        <ContentGrid>
          <Left>
            <VideoContainer>{player}</VideoContainer>
          </Left>
        </ContentGrid>
      )}

      {width === 'right' && (
        <ContentGrid>
          <Right>
            <VideoContainer>{player}</VideoContainer>
          </Right>
        </ContentGrid>
      )}

      {width === 'leftandtext' && (
        <ContentGrid>
          <LeftAndText>
            <VideoContainer>{player}</VideoContainer>
          </LeftAndText>
        </ContentGrid>
      )}

      {width === 'rightandtext' && (
        <ContentGrid>
          <RightAndText>
            <VideoContainer>{player}</VideoContainer>
          </RightAndText>
        </ContentGrid>
      )}

      {(caption || source) && (
        <ContentGrid>
          <TextWidth>
            {labelType === 'bottom' && (
              <>
                {title && <Title className='bottom'>{title}</Title>}
                {/* {caption && <Lead className='bottom'>{caption}</Lead>} */}
              </>
            )}
            <Caption
              className={
                labelType + ' ' + (typeof title !== 'string' && 'true')
              }
            >
              {(labelType === 'bottom' || labelType === 'top') && caption}
              {source && <Source>{source}</Source>}
            </Caption>
          </TextWidth>
        </ContentGrid>
      )}
    </>
  )
}

const VideoContainer = styled.div<{ tenant?: string }>`
  overflow: hidden;
  /* position: relative; */
  /* margin-bottom: 3rem; */
  display: flex;
  flex-direction: column;
  align-items: center;
  grid-column: 1/-1;
  width: 100%;
  video {
    width: 100%;
  }
`

const Caption = styled.figcaption`
  color: var(--text-color);
  font-family: var(--font-sans-serif);
  font-size: 1.5rem;
  line-height: 2.1rem;

  &.top {
    margin-top: 1.5rem;
  }

  &.bottom.true {
    margin-top: 1.5rem;
  }

  .source {
    margin-top: 0.5em;
    font-weight: 400;
    color: var(--gray-3);
    font-size: 1.2rem;
    line-height: 1.8rem;
  }
`
const Source = styled.div`
  margin-top: 0.5em;
  font-weight: 400;
  color: var(--gray-3);
  font-size: 1.2rem;
  line-height: 1.8rem;
  margin-bottom: 2em;
  &.bottom {
    margin-top: 0.3rem;
  }
`

const Title = styled.h3`
  color: var(--text-color);
  margin: 0;
  font-size: 18px !important;
  line-height: 2.4rem;
  margin-bottom: 0.5rem;

  &.bottom {
    margin-top: 1.5rem;
    font-size: 1.5rem !important;
    line-height: 2.1rem;
    margin-bottom: 0;
  }
`
const Lead = styled.div`
  color: var(--text-color);
  font-size: 15px;
  width: 100%;
  font-style: normal;
  font-weight: 400;
  line-height: 2.3rem;
  margin: 0;
  margin-bottom: 1rem;
  &.bottom {
    line-height: 2.1rem;
    margin-bottom: 0;
  }
  &.top {
    margin-bottom: 1.5rem;
  }
`
